import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import Cookies from "universal-cookie";
import { db } from "../../firebase";
import BookingStripeButton from "../Checkout/BookingStripeButton";

const BRAND = "#194049";
const timeRanges = ["11am - 1pm", "1pm - 3pm", "4pm - 6pm", "6pm - 8pm"];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseTimeHour(label) {
  const match = label.match(/^(\d+)(am|pm)$/i);
  if (!match) return 0;
  let h = parseInt(match[1], 10);
  if (match[2].toLowerCase() === "pm" && h !== 12) h += 12;
  if (match[2].toLowerCase() === "am" && h === 12) h = 0;
  return h;
}

function combineDateAndTime(date, timeLabel) {
  const startPart = timeLabel.split(" - ")[0].trim();
  const hour = parseTimeHour(startPart);
  const d = new Date(date);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

const moneyGBP = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const fmtDate = (date, opts) =>
  date.toLocaleDateString("en-GB", opts);

// ── Step progress bar ─────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = ["Category", "Service", "Schedule"];
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: { xs: 4, md: 5 }, mx: "auto", maxWidth: 380 }}>
      {steps.map((label, i) => {
        const num = i + 1;
        const done = step > num;
        const active = step === num;
        return (
          <React.Fragment key={label}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, minWidth: 72 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "50%",
                bgcolor: done || active ? BRAND : "#e5e7eb",
                color: done || active ? "#fff" : "#9ca3af",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, transition: "all 0.25s",
                boxShadow: active ? `0 0 0 5px ${BRAND}20` : "none",
              }}>
                {done ? <CheckIcon sx={{ fontSize: 16 }} /> : num}
              </Box>
              <Typography sx={{
                fontSize: 10, fontWeight: active ? 700 : 500,
                color: active ? BRAND : done ? "#374151" : "#9ca3af",
                letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                {label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ flex: 1, height: 2, bgcolor: step > num ? BRAND : "#e5e7eb", mt: 2.25, transition: "background 0.3s" }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── Back link ─────────────────────────────────────────────────────────────────
function BackBtn({ label, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.25,
        color: "#6b7280", fontSize: 13, cursor: "pointer",
        "&:hover": { color: BRAND }, transition: "color 0.15s",
      }}
    >
      <ChevronLeftIcon sx={{ fontSize: 17 }} />
      {label}
    </Box>
  );
}

// ── Category card ─────────────────────────────────────────────────────────────
function CategoryCard({ title, description, onSelect }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "stretch", bgcolor: "#fff",
        borderRadius: "14px", border: "1.5px solid",
        borderColor: hovered ? BRAND : "#e5e7eb",
        overflow: "hidden", cursor: "pointer",
        boxShadow: hovered ? `0 6px 24px ${BRAND}18` : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "all 0.2s",
      }}
    >
      <Box sx={{ width: 5, bgcolor: hovered ? BRAND : "#e5e7eb", flexShrink: 0, transition: "background 0.2s" }} />
      <Box sx={{ flex: 1, px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
        <Typography sx={{ fontSize: { xs: 16, md: 17 }, fontWeight: 600, color: "#111827", mb: description ? 0.5 : 0 }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", pr: 2.5, color: hovered ? BRAND : "#d1d5db", transition: "color 0.2s" }}>
        <ChevronRightIcon />
      </Box>
    </Box>
  );
}

// ── Service card ──────────────────────────────────────────────────────────────
function ServiceCard({ name, price, timeframe, onSelect }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        bgcolor: "#fff", borderRadius: "14px", border: "1.5px solid",
        borderColor: hovered ? BRAND : "#e5e7eb",
        px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 },
        cursor: "pointer",
        boxShadow: hovered ? `0 6px 24px ${BRAND}18` : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "all 0.2s",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography sx={{ fontSize: { xs: 15, md: 16 }, fontWeight: 600, color: "#111827", flex: 1, pr: 1, lineHeight: 1.35 }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 700, color: BRAND, whiteSpace: "nowrap" }}>
          {moneyGBP(price)}
        </Typography>
      </Box>
      {Array.isArray(timeframe) && timeframe.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <AccessTimeIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
          <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>{timeframe.join(" · ")}</Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.5,
          fontSize: 12, fontWeight: 600,
          color: hovered ? "#fff" : BRAND,
          bgcolor: hovered ? BRAND : `${BRAND}12`,
          px: 2, py: 0.625, borderRadius: "8px", transition: "all 0.2s",
        }}>
          Book this
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Custom date picker ────────────────────────────────────────────────────────
function DatePicker({ selectedDate, onChange }) {
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const selStr = selectedDate.toDateString();
  const todayStr = todayMidnight.toDateString();

  return (
    <Box>
      {/* Month nav */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box
          onClick={prevMonth}
          sx={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6", color: BRAND }, transition: "all 0.15s" }}
        >
          <ChevronLeftIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>
            {MONTH_NAMES[viewMonth]}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#9ca3af", mt: -0.25 }}>{viewYear}</Typography>
        </Box>
        <Box
          onClick={nextMonth}
          sx={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6", color: BRAND }, transition: "all 0.15s" }}
        >
          <ChevronRightIcon sx={{ fontSize: 19 }} />
        </Box>
      </Box>

      {/* Day headers */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 1 }}>
        {DAY_LABELS.map((d) => (
          <Typography key={d} sx={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af", pb: 1.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {cells.map((day, idx) => {
          if (!day) return <Box key={`e${idx}`} />;
          const cellDate = new Date(viewYear, viewMonth, day);
          cellDate.setHours(0, 0, 0, 0);
          const isPast = cellDate < todayMidnight;
          const isSelected = cellDate.toDateString() === selStr;
          const isToday = cellDate.toDateString() === todayStr;

          return (
            <Box
              key={`d${day}`}
              onClick={() => !isPast && onChange(cellDate)}
              sx={{
                aspectRatio: "1",
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "50%", position: "relative",
                cursor: isPast ? "default" : "pointer",
                bgcolor: isSelected ? BRAND : "transparent",
                color: isSelected ? "#fff" : isPast ? "#d1d5db" : "#111827",
                fontSize: { xs: 13, md: 14 },
                fontWeight: isSelected || isToday ? 700 : 400,
                border: isToday && !isSelected ? `2px solid ${BRAND}` : "2px solid transparent",
                "&:hover": isPast || isSelected ? {} : { bgcolor: `${BRAND}14`, color: BRAND },
                transition: "all 0.12s",
                userSelect: "none",
              }}
            >
              {day}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Time slot row ─────────────────────────────────────────────────────────────
function TimeSlot({ label, active, disabled, onClick }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 2.5, py: 1.5, borderRadius: "12px", border: "1.5px solid",
        borderColor: active ? BRAND : disabled ? "#f3f4f6" : "#e5e7eb",
        bgcolor: active ? BRAND : disabled ? "#fafafa" : "#fff",
        color: active ? "#fff" : disabled ? "#d1d5db" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.65 : 1, transition: "all 0.15s",
        "&:hover": disabled || active ? {} : { borderColor: BRAND, color: BRAND, bgcolor: `${BRAND}08` },
      }}
    >
      <AccessTimeIcon sx={{ fontSize: 15, flexShrink: 0, opacity: disabled ? 0.5 : 1 }} />
      <Typography sx={{ fontSize: 13, fontWeight: active ? 600 : 400, flex: 1, color: "inherit" }}>
        {label}
      </Typography>
      {disabled && (
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#ef4444", letterSpacing: "0.05em" }}>
          FULL
        </Typography>
      )}
      {active && <CheckIcon sx={{ fontSize: 15 }} />}
    </Box>
  );
}

// ── Booking summary chip ──────────────────────────────────────────────────────
function SummaryChip({ category, service, price }) {
  return (
    <Box sx={{
      display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 1,
      bgcolor: "#f0fdf4", border: "1px solid #bbf7d0",
      borderRadius: "10px", px: 2, py: 0.875, mb: 3,
    }}>
      <ContentCutIcon sx={{ fontSize: 14, color: "#16a34a" }} />
      <Typography sx={{ fontSize: 13, color: "#166534", fontWeight: 500 }}>
        {category} · {service}
      </Typography>
      <Box sx={{ width: "1px", height: 14, bgcolor: "#86efac" }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND }}>
        {moneyGBP(price)}
      </Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const DRAFT_KEY = "strandz-booking-draft";

export const BookingBody = () => {
  const [catalogueCategories, setCatalogueCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedTimes, setBookedTimes] = useState(new Set());
  const [showPayment, setShowPayment] = useState(false);
  const [checking, setChecking] = useState(false);
  const [proceedError, setProceedError] = useState("");

  const navigate = useNavigate();
  const cookies = new Cookies();
  const userId = cookies.get("strandz-user-tokenID") || null;
  const isLoggedIn = !!userId || !!localStorage.getItem("user");

  useEffect(() => {
    (async () => {
      try {
        const [catSnap, svcSnap] = await Promise.all([
          getDocs(query(collection(db, "service-categories"), orderBy("createdAt", "asc"))),
          getDocs(query(collection(db, "services"), orderBy("createdAt", "asc"))),
        ]);
        const svcDocs = svcSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const cats = catSnap.docs.map((d) => {
          const catData = { id: d.id, ...d.data() };
          return {
            ...catData,
            services: svcDocs
              .filter((s) => s.categoryId === d.id)
              .map((s) => ({ name: s.name, price: s.price, timeframe: s.timeframe })),
          };
        });
        setCatalogueCategories(cats);

        // Restore saved draft after login redirect
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          try {
            const draft = JSON.parse(raw);
            localStorage.removeItem(DRAFT_KEY);
            if (draft.category) setSelectedCategory(draft.category);
            if (draft.service) setSelectedService(draft.service);
            if (draft.date) setSelectedDate(new Date(draft.date));
            if (draft.time) setSelectedTime(draft.time);
          } catch {}
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedService) return;

    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);

    const y = selectedDate.getFullYear();
    const mo = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    const dateStr = `${y}-${mo}-${d}`;

    Promise.all([
      getDocs(query(collection(db, "booking-list"), where("status", "==", "paid"), where("time", ">=", dayStart.toISOString()), where("time", "<=", dayEnd.toISOString()))),
      getDoc(doc(db, "availability", dateStr)),
    ])
      .then(([bookSnap, availSnap]) => {
        const blocked = new Set(bookSnap.docs.map((d) => d.data().time));
        if (availSnap.exists()) {
          const { slots } = availSnap.data();
          const ranges = slots.includes("all") ? timeRanges : slots;
          ranges.forEach((slot) => blocked.add(combineDateAndTime(selectedDate, slot)));
        }
        setBookedTimes(blocked);
      })
      .catch(console.error);
  }, [selectedDate, selectedService]);

  const resetToCategories = () => {
    localStorage.removeItem(DRAFT_KEY);
    setSelectedCategory(null);
    setSelectedService(null);
    setSelectedDate(new Date());
    setSelectedTime("");
    setBookedTimes(new Set());
    setShowPayment(false);
    setProceedError("");
  };

  const resetToServices = () => {
    setSelectedService(null);
    setSelectedDate(new Date());
    setSelectedTime("");
    setBookedTimes(new Set());
    setShowPayment(false);
    setProceedError("");
  };

  const handleProceed = async () => {
    if (!isLoggedIn) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        category: selectedCategory,
        service: selectedService,
        date: selectedDate.toISOString(),
        time: selectedTime,
      }));
      navigate("/login", { state: { from: "/bookings" } });
      return;
    }
    setChecking(true);
    setProceedError("");
    try {
      const isoTime = combineDateAndTime(selectedDate, selectedTime);
      const y = selectedDate.getFullYear();
      const mo = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const d = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${y}-${mo}-${d}`;

      const [availSnap, bookingSnap] = await Promise.all([
        getDoc(doc(db, "availability", dateStr)),
        getDocs(query(collection(db, "booking-list"), where("time", "==", isoTime), where("status", "==", "paid"))),
      ]);

      if (availSnap.exists()) {
        const { slots } = availSnap.data();
        if (slots.includes("all") || slots.includes(selectedTime)) {
          setProceedError("This time slot is unavailable. Please pick another time.");
          return;
        }
      }
      if (!bookingSnap.empty) {
        setProceedError("This time slot has already been booked. Please pick another time slot.");
        return;
      }
      setShowPayment(true);
    } catch (e) {
      console.error(e);
      setProceedError("Something went wrong. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const selectedDateTime = selectedTime ? combineDateAndTime(selectedDate, selectedTime) : null;
  const canSubmit = Boolean(selectedCategory && selectedService && selectedTime);

  // ── VIEW 1: Categories ──────────────────────────────────────────────────────
  if (!selectedCategory) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <StepBar step={1} />
        <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: "#111827", letterSpacing: "-0.025em", mb: 0.75 }}>
          Choose a Category
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 4 }}>
          Select the type of service you'd like to book
        </Typography>
        {catalogueCategories.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
            {catalogueCategories.map((cat) => (
              <CategoryCard
                key={cat.name}
                title={cat.name}
                description={cat.description}
                onSelect={() => {
                  setSelectedCategory(cat);
                  setSelectedService(null);
                  setSelectedDate(new Date());
                  setSelectedTime("");
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // ── VIEW 2: Services ────────────────────────────────────────────────────────
  if (!selectedService) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <StepBar step={2} />
        <BackBtn label="Back to Categories" onClick={resetToCategories} />
        <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: "#111827", letterSpacing: "-0.025em", mb: 0.75, mt: 2.5 }}>
          {selectedCategory.name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 4 }}>
          Choose the service you'd like to book
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          {selectedCategory.services.map((svc) => (
            <ServiceCard
              key={svc.name}
              name={svc.name}
              price={svc.price}
              timeframe={svc.timeframe}
              onSelect={() => {
                setSelectedService(svc);
                setSelectedDate(new Date());
                setSelectedTime("");
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // ── VIEW 3: Date, Time & Confirm ────────────────────────────────────────────
  const timeSlots =
    Array.isArray(selectedService?.timeframe) && selectedService.timeframe.length > 0
      ? selectedService.timeframe
      : timeRanges;

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
      <StepBar step={3} />

      {/* Breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <BackBtn label="Categories" onClick={resetToCategories} />
        <Typography sx={{ fontSize: 12, color: "#d1d5db" }}>/</Typography>
        <BackBtn label={selectedCategory.name} onClick={resetToServices} />
      </Box>

      <SummaryChip
        category={selectedCategory.name}
        service={selectedService.name}
        price={selectedService.price}
      />

      {/* Calendar + Time grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, mb: 2.5 }}>
        {/* Calendar card */}
        <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2.5, md: 3 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", mb: 3 }}>
            Select Date
          </Typography>
          <DatePicker
            selectedDate={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setSelectedTime("");
              setShowPayment(false);
              setProceedError("");
            }}
          />
          {selectedTime && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #f3f4f6" }}>
              <Typography sx={{ fontSize: 11, color: "#9ca3af", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Selected
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: BRAND }}>
                {fmtDate(selectedDate, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Time slots card */}
        <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2.5, md: 3 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
            Select Time
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500, mb: 2.5 }}>
            {fmtDate(selectedDate, { weekday: "long", month: "long", day: "numeric" })}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {timeSlots.map((time) => {
              const timeIso = combineDateAndTime(selectedDate, time);
              const isBooked = bookedTimes.has(timeIso);
              return (
                <TimeSlot
                  key={time}
                  label={time}
                  active={selectedTime === time}
                  disabled={isBooked}
                  onClick={() => {
                    setSelectedTime(time);
                    setShowPayment(false);
                    setProceedError("");
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Proceed panel */}
      {canSubmit && !showPayment && (
        <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2.5, md: 3 } }}>
          <Box sx={{
            display: "flex", flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2,
          }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                {fmtDate(selectedDate, { weekday: "short", day: "numeric", month: "long" })} · {selectedTime}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                {selectedService.name} — {moneyGBP(selectedService.price)}
              </Typography>
            </Box>
            <Box
              onClick={!checking ? handleProceed : undefined}
              sx={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 1,
                px: 3.5, py: 1.5, borderRadius: "10px", flexShrink: 0,
                bgcolor: checking ? "#9ca3af" : BRAND, color: "#fff",
                fontSize: 14, fontWeight: 600, cursor: checking ? "default" : "pointer",
                "&:hover": checking ? {} : { bgcolor: "#0f2a31" },
                transition: "all 0.15s", userSelect: "none",
              }}
            >
              {checking && <CircularProgress size={14} sx={{ color: "#fff" }} />}
              {checking ? "Checking…" : "Proceed"}
            </Box>
          </Box>
          {proceedError && (
            <Box sx={{ mt: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", px: 2, py: 1.25 }}>
              <Typography sx={{ fontSize: 13, color: "#dc2626" }}>{proceedError}</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Payment panel */}
      {canSubmit && showPayment && (
        <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 2.5, mb: 2.5, borderBottom: "1px solid #f3f4f6" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#22c55e", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>
              Slot available — complete your booking below
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, mb: 0.5 }}>
              Booking Summary
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
              {fmtDate(selectedDate, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.25 }}>
              {selectedTime} · {selectedCategory.name} / {selectedService.name}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: BRAND, mt: 1 }}>
              {moneyGBP(selectedService.price)}
            </Typography>
          </Box>
          <BookingStripeButton
            amount={selectedService?.price}
            selectedDateTime={selectedDateTime}
            userId={userId}
            category={selectedCategory?.name}
            service={selectedService?.name}
          />
        </Box>
      )}
    </Box>
  );
};
